import { useMemo } from 'react';
import { Configuration, OrdersApi } from '@/api';
import { OrderFilters } from '@/api/OrdersApi';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const DEFAULT_LIMIT = 5;

const useOrderAttentionItemsQuery = (
  filters?: OrderFilters,
  limit: number = DEFAULT_LIMIT,
  enabled = true,
  offset = 0,
) => {
  const auth = useAuth();
  const ordersApi = useMemo(() => new OrdersApi(new Configuration(auth)), [auth]);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['order-attention-items', filters, offset, limit],
    enabled,
    queryFn: () => ordersApi.listOrderAttentionItems(filters, offset, limit),
  });

  const resolveMutation = useMutation({
    mutationFn: ({
      orderId,
      incompleteDeliveryId,
    }: {
      orderId: string;
      incompleteDeliveryId: string;
    }) => ordersApi.resolveOrderIncompleteDelivery(orderId, incompleteDeliveryId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'order-attention-items',
      });
    },
  });

  return {
    items: query.data?.items ?? [],
    totalCount: query.data?.totalCount ?? query.data?.estimatedTotalCount ?? 0,
    estimatedTotalCount: query.data?.estimatedTotalCount ?? 0,
    isEstimatedTotalCount: query.data?.totalCount === undefined && query.data !== undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    resolveIncompleteDelivery: resolveMutation.mutateAsync,
    isResolvingIncompleteDelivery: resolveMutation.isPending,
  };
};

export default useOrderAttentionItemsQuery;
